import {
  EMPTY_SAVED_STATE,
  readSavedState,
  writeSavedState,
} from '~components/DataTable/dataTableSavedState';

type SavedState = ReturnType<typeof readSavedState>;

interface SharedLayout {
  listeners: Set<() => void>;
  resetListeners: Set<() => void>;
  state: SavedState;
}

// Tables mounted with the same id share one layout, so a change in one reaches the
// others. An entry lives only while a table with its id is mounted.
const sharedLayouts = new Map<string, SharedLayout>();

function sharedLayoutFor(tableId: string) {
  let layout = sharedLayouts.get(tableId);

  if (!layout) {
    layout = {
      listeners: new Set(),
      resetListeners: new Set(),
      state: readSavedState(tableId),
    };
    sharedLayouts.set(tableId, layout);
  }

  return layout;
}

function releaseIfUnused(tableId: string, layout: SharedLayout) {
  const isUnused = layout.listeners.size === 0 && layout.resetListeners.size === 0;

  if (isUnused && sharedLayouts.get(tableId) === layout) {
    sharedLayouts.delete(tableId);
  }
}

function publish(tableId: string, layout: SharedLayout, state: SavedState) {
  layout.state = state;
  writeSavedState(tableId, state);
  layout.listeners.forEach((listener) => listener());
}

export function getSharedLayoutState(tableId: string) {
  return sharedLayoutFor(tableId).state;
}

export function subscribeToSharedLayout(tableId: string, listener: () => void) {
  const layout = sharedLayoutFor(tableId);
  const isFirstTableWithThisId = layout.listeners.size === 0;

  // A render that never mounted, or a write after the last unmount, can leave an
  // entry behind, so the first table to subscribe re-reads storage.
  if (isFirstTableWithThisId) {
    const storedState = readSavedState(tableId);
    const storageChanged = JSON.stringify(storedState) !== JSON.stringify(layout.state);

    if (storageChanged) {
      layout.state = storedState;
    }
  }

  layout.listeners.add(listener);

  return () => {
    layout.listeners.delete(listener);
    releaseIfUnused(tableId, layout);
  };
}

export function subscribeToSharedLayoutResets(tableId: string, listener: () => void) {
  const layout = sharedLayoutFor(tableId);
  layout.resetListeners.add(listener);

  return () => {
    layout.resetListeners.delete(listener);
    releaseIfUnused(tableId, layout);
  };
}

export function updateSharedLayout(
  tableId: string,
  update: (previous: SavedState) => SavedState,
) {
  const layout = sharedLayoutFor(tableId);
  const nextState = update(layout.state);
  const isUnchanged = JSON.stringify(nextState) === JSON.stringify(layout.state);

  if (!isUnchanged) {
    publish(tableId, layout, nextState);
  }

  releaseIfUnused(tableId, layout);
}

// Writes an empty state rather than removing the key, so the next mount doesn't
// fall back to the legacy DataGrid state it was migrated from.
export function resetSharedLayout(tableId: string) {
  const layout = sharedLayoutFor(tableId);
  publish(tableId, layout, { ...EMPTY_SAVED_STATE });
  layout.resetListeners.forEach((listener) => listener());
  releaseIfUnused(tableId, layout);
}
